"use client";

import { useEffect, useState } from "react";
import { isTrustedMessageSource } from "../message-source";
import { useWidgetEnv } from "../use-widget-env";
import { requestHostViewport } from "./request-host-viewport";

/** The HOST viewport in the iframe embed; `height` is `null` when the report carried none. */
export interface HostViewport {
  width: number;
  height: number | null;
}

/**
 * The HOST viewport in the iframe embed (inside the frame `innerWidth` / `innerHeight` are the frame's
 * own). The loader reports it with `wexio:host-viewport:v1`. `null` for in-DOM embeds (they read the
 * window) and until the loader reports (an older loader never does), so callers keep their "unknown"
 * behaviour.
 */
export function useIframeHostViewport(): HostViewport | null {
  const iframe = useWidgetEnv().kind === "iframe";
  const [viewport, setViewport] = useState<HostViewport | null>(null);
  useEffect(() => {
    if (!iframe) return;
    const onMessage = (e: MessageEvent) => {
      if (!isTrustedMessageSource(e, "iframe")) return;
      const d = e.data;
      if (!d || typeof d !== "object" || d.type !== "wexio:host-viewport:v1") return;
      if (typeof d.width !== "number" || d.width <= 0) return;
      const height = typeof d.height === "number" && d.height > 0 ? d.height : null;
      setViewport((v) =>
        v && v.width === d.width && v.height === height ? v : { width: d.width, height },
      );
    };
    window.addEventListener("message", onMessage);
    requestHostViewport();
    return () => window.removeEventListener("message", onMessage);
  }, [iframe]);
  return viewport;
}

/** The HOST viewport width in the iframe embed (see `useIframeHostViewport`). */
export function useIframeHostWidth(): number | null {
  return useIframeHostViewport()?.width ?? null;
}
